/**
 * Reporting Types
 *
 * Config-driven custom reports. A ReportConfig is a UI-facing query spec
 * (entity + dimensions + measures + filters) that the backend compiles into
 * SQL. UI-facing descriptors (ReportFieldMeta) never carry SQL.
 */
import { z } from 'zod';
import { SortDirectionSchema } from './pagination.js';
// =============================================================================
// Primitives
// =============================================================================
/** Logical field id for the "Count Records" measure (COUNT(*)). Backend maps this id → sqlExpr '*'. */
export const COUNT_FIELD_ID = 'count';
/** Entities a report can run over. Derived from REPORT_ENTITIES. */
export const REPORT_ENTITIES = [
    'deals',
    'clients',
    'contacts',
    'meetings',
    'users',
    'action_items',
];
/** Supported measure aggregations. */
export const REPORT_AGGS = ['count', 'count_distinct', 'sum', 'avg', 'min', 'max'];
/** Supported filter operators. */
export const REPORT_FILTER_OPS = [
    'eq',
    'neq',
    'in',
    'gt',
    'gte',
    'lt',
    'lte',
    'contains',
    'between',
    'is_null',
];
// =============================================================================
// Config building blocks
// =============================================================================
/** Report kind. Drives which config fields/builder UI apply. */
export const REPORT_TYPES = [
    'insight',
    'metric',
    'funnel',
    'time_in_stage',
    'stage_changed',
    'historical',
];
/** Funnel sub-modes (Attio: Funnel / Funnel Metric / Funnel Steps). */
export const FUNNEL_MODES = ['funnel', 'funnel_metric', 'funnel_steps'];
/** Time-in-stage aggregation. */
export const TIME_IN_STAGE_AGGS = ['max', 'avg', 'min'];
/** Date bucket interval for time-series report types. */
export const REPORT_INTERVALS = ['day', 'week', 'month', 'quarter', 'year'];
/** Supported visualization types (Attio-parity insight charts). */
export const REPORT_VIZ_TYPES = [
    'table',
    'bar',
    'line',
    'area',
    'donut',
    'pie',
    'number',
    'funnel',
    'map',
];
/** Relative date range presets for time-series reports. */
export const REPORT_RANGE_PRESETS = ['7d', '1m', '3m', '6m', '12m', 'all', 'ptd'];
// =============================================================================
// Zod schemas
// =============================================================================
export const ReportDimensionSchema = z.object({
    field: z.string(),
    dateTrunc: z.enum(REPORT_INTERVALS).optional(),
    includedValues: z.array(z.union([z.string(), z.number()])).optional(),
    includeEmpty: z.boolean().optional(),
});
export const ReportMeasureSchema = z.object({
    field: z.string(),
    agg: z.enum(REPORT_AGGS),
    alias: z.string().optional(),
});
export const ReportFilterSchema = z.object({
    field: z.string(),
    op: z.enum(REPORT_FILTER_OPS),
    value: z.unknown().optional(),
    values: z.array(z.unknown()).optional(),
});
export const ReportSortSchema = z.object({
    field: z.string(),
    dir: SortDirectionSchema,
});
export const ReportTargetSchema = z.object({
    kind: z.enum(['quota', 'value']),
    quotaId: z.string().uuid().optional(),
    value: z.number().optional(),
});
export const ReportVisualizationSchema = z.object({
    type: z.enum(REPORT_VIZ_TYPES),
    stacked: z.boolean().optional(),
    showAxis: z.boolean().optional(),
    breakdown: z.string().optional(),
    target: ReportTargetSchema.optional(),
    numberLabel: z.string().optional(),
    mapView: z.enum(['flat', 'globe']).optional(),
    showDataLabels: z.boolean().optional(),
    sortByValue: z.boolean().optional(),
    showZeros: z.boolean().optional(),
});
export const ReportFunnelConfigSchema = z.object({
    statusField: z.string(),
    includedStages: z.array(z.string()),
    mode: z.enum(FUNNEL_MODES).optional(),
    metric: ReportMeasureSchema.optional(),
});
export const ReportTimeInStageConfigSchema = z.object({
    statusField: z.string(),
    includedStages: z.array(z.string()),
    aggregation: z.enum(TIME_IN_STAGE_AGGS),
    groupBy: z.string().optional(),
});
export const ReportStageChangedConfigSchema = z.object({
    statusField: z.string(),
    includedStages: z.array(z.string()),
    interval: z.enum(REPORT_INTERVALS),
    metric: ReportMeasureSchema.optional(),
});
export const ReportHistoricalConfigSchema = z.object({
    dateField: z.string(),
    interval: z.enum(REPORT_INTERVALS),
});
export const ReportComparisonSchema = z.object({
    mode: z.literal('previous_period'),
});
export const ReportDateRangeSchema = z.object({
    preset: z.enum(REPORT_RANGE_PRESETS).optional(),
    from: z.string().optional(),
    to: z.string().optional(),
});
export const ReportEntitySchema = z.union([
    z.enum(REPORT_ENTITIES),
    z.string().regex(/^object:[a-z][a-z0-9_]*$/),
]);
export const ReportConfigSchema = z.object({
    entity: ReportEntitySchema,
    reportType: z.enum(REPORT_TYPES).optional(),
    dimensions: z.array(ReportDimensionSchema),
    measures: z.array(ReportMeasureSchema),
    filters: z.array(ReportFilterSchema),
    sort: z.array(ReportSortSchema).optional(),
    limit: z.number().optional(),
    visualization: ReportVisualizationSchema.optional(),
    funnel: ReportFunnelConfigSchema.optional(),
    timeInStage: ReportTimeInStageConfigSchema.optional(),
    stageChanged: ReportStageChangedConfigSchema.optional(),
    historical: ReportHistoricalConfigSchema.optional(),
    compare: ReportComparisonSchema.optional(),
    dateRange: ReportDateRangeSchema.optional(),
});
export const SavedReportSchema = z.object({
    id: z.string().uuid(),
    orgId: z.string().min(1),
    createdBy: z.string(),
    name: z.string().min(1),
    entity: ReportEntitySchema,
    config: ReportConfigSchema,
    isShared: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
